import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, catchError, map, tap, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { AlertifyService } from './alertify.service';
import { ProjectService } from './project.service';
import { AuthService } from './auth.service';
import {
  BidProjectType, CatalogService, RateCard, RcAddon, RcTier, ServiceType, TemplateRow
} from '../models/bid-settings';

interface BidSettingsResponse {
  catalog: ApiCatalogService[];
  templates: ApiTemplateRow[];
  rateCards: ApiRateCard[];
}

interface ApiCatalogService {
  id: string;
  name: string;
  serviceType: string;
  fixedRate: number | null;
  hourlyRate: number | null;
  markup: number;
  pts: string[];
  rateCardId?: string;
}

interface ApiTemplateRow {
  id: string;
  projectTypeId: string;
  projectTypeName: string;
  name: string;
  serviceType: string;
  fixedRate: number | null;
  hourlyRate: number | null;
  hours: number | null;
  markup: number;
  defaultOn: boolean;
  rateCardId?: string;
}

interface ApiRcTier  { id: string; name: string; rate: number; }
interface ApiRcAddon { id: string; name: string; price: number; }

interface ApiRateCard {
  id: string;
  name: string;
  type: string;
  tiers: ApiRcTier[];
  addons: ApiRcAddon[];
}

@Injectable({ providedIn: 'root' })
export class BidSettingsService {
  private readonly baseUrl = `${environment.apiEndpoint}/bid-settings`;
  private readonly _ready = new ReplaySubject<void>(1);
  readonly ready$ = this._ready.asObservable();

  projectTypes: BidProjectType[] = [];
  catalog: CatalogService[] = [];
  ptData: Record<string, TemplateRow[]> = {};
  rateCards: RateCard[] = [];
  loading = true;

  constructor(
    private http: HttpClient,
    private alertify: AlertifyService,
    private projectService: ProjectService,
    private auth: AuthService
  ) {
    // Bid settings (project types, catalog, rate cards) are internal-only and forbidden for vendor
    // sessions. A vendor only views their own bid (no Services pricing), so skip the load entirely
    // and mark ready so any consumers don't hang.
    if (this.auth.isVendor() || this.auth.isVendorAdmin()) {
      this.loading = false;
      this._ready.next();
    } else {
      this.loadAll();
    }
  }

  reload(): void { this.loadAll(); }

  private loadAll(): void {
    this.loading = true;
    this.projectService.GetProjectTypes(null, false).subscribe({
      next: types => {
        this.projectTypes = types
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(t => ({ id: t.id, name: t.name }));
        this.ptData = Object.fromEntries(this.projectTypes.map(pt => [pt.id, []]));
        this.loadSettings();
      },
      error: () => {
        this.loading = false;
        this.alertify.error('Failed to load project types');
      }
    });
  }

  private loadSettings(): void {
    this.http.get<BidSettingsResponse>(this.baseUrl).subscribe({
      next: res => {
        this.catalog = res.catalog
          .map(s => this.mapCatalog(s))
          .sort((a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : 0);
        for (const pt of this.projectTypes) {
          this.ptData[pt.id] = res.templates
            .filter(t => t.projectTypeId === pt.id)
            .map(t => this.mapTemplate(t));
        }
        this.rateCards = (res.rateCards ?? []).map(rc => ({
          id:     rc.id,
          name:   rc.name,
          type:   rc.type,
          tiers:  rc.tiers.map(t => ({ id: t.id, name: t.name, rate: t.rate })),
          addons: rc.addons.map(a => ({ id: a.id, name: a.name, price: a.price })),
        }));
        this.loading = false;
        this._ready.next();
      },
      error: () => {
        this.loading = false;
        this.alertify.error('Failed to load bid settings');
      }
    });
  }

  // ── Catalog ────────────────────────────────────────────────────────────────

  addCatalogService(name: string, type: ServiceType, rateCardId?: string): Observable<void> {
    return this.http
      .post<ApiCatalogService>(`${this.baseUrl}/catalog`, { name, serviceType: type, rateCardId })
      .pipe(
        tap(s => {
          this.catalog.push(this.mapCatalog(s));
          this.alertify.success('Service added');
        }),
        map(() => undefined as void),
        catchError(err => { this.alertify.error('Failed to add service'); return throwError(() => err); })
      );
  }

  updateCatalogService(svc: CatalogService): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/catalog/${svc.id}`, {
      name:        svc.name,
      serviceType: svc.type,
      fixedRate:   svc.fixedRate,
      hourlyRate:  svc.hourlyRate,
      markup:      svc.markup,
      pts:         svc.pts,
      rateCardId:  svc.rateCardId ?? null,
    }).pipe(
      tap(() => this.alertify.success('Service updated')),
      catchError(err => { this.alertify.error('Failed to update service'); return throwError(() => err); })
    );
  }

  deleteCatalogService(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/catalog/${id}`).pipe(
      tap(() => {
        const i = this.catalog.findIndex(s => s.id === id);
        if (i !== -1) this.catalog.splice(i, 1);
        this.alertify.success('Service deleted');
      }),
      catchError(err => { this.alertify.error('Failed to delete service'); return throwError(() => err); })
    );
  }

  // ── Templates ──────────────────────────────────────────────────────────────

  addTemplate(projectTypeId: string, svc: CatalogService, defaultOn = false): Observable<void> {
    return this.http
      .post<ApiTemplateRow>(`${this.baseUrl}/templates`, {
        projectTypeId,
        catalogServiceId: svc.id,
        fixedRate:        svc.fixedRate ?? null,
        hourlyRate:       svc.hourlyRate ?? null,
        hours:            svc.type === 'Labor' ? 8 : null,
        markup:           svc.markup ?? 0,
        defaultOn,
      })
      .pipe(
        tap(t => {
          const row = this.mapTemplate(t);
          row.name = row.name || svc.name;
          row.type = row.type || svc.type;
          (this.ptData[projectTypeId] ??= []).push(row);
          this.alertify.success('Service added to template');
        }),
        map(() => undefined as void),
        catchError(err => { this.alertify.error('Failed to add service to template'); return throwError(() => err); })
      );
  }

  updateTemplate(row: TemplateRow): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/templates/${row.id}`, {
      serviceType: row.type,
      fixedRate:   row.fixedRate,
      hourlyRate:  row.hourlyRate,
      hours:       row.hours,
      markup:      row.markup,
      defaultOn:   row.defaultOn,
    }).pipe(
      tap(() => this.alertify.success('Template updated')),
      catchError(err => { this.alertify.error('Failed to update template'); return throwError(() => err); })
    );
  }

  deleteTemplate(id: string, projectTypeId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/${id}`).pipe(
      tap(() => {
        const rows = this.ptData[projectTypeId];
        const i = rows?.findIndex(r => r.id === id) ?? -1;
        if (i !== -1) rows.splice(i, 1);
        this.alertify.success('Service removed from template');
      }),
      catchError(err => { this.alertify.error('Failed to remove service from template'); return throwError(() => err); })
    );
  }

  // ── Rate Cards ─────────────────────────────────────────────────────────────

  addRateCard(name: string): Observable<void> {
    return this.http
      .post<ApiRateCard>(`${this.baseUrl}/rate-cards`, { name })
      .pipe(
        tap(rc => {
          this.rateCards.push({ id: rc.id, name: rc.name, type: rc.type, tiers: [], addons: [] });
          this.alertify.success('Rate card added');
        }),
        map(() => undefined as void),
        catchError(err => { this.alertify.error('Failed to add rate card'); return throwError(() => err); })
      );
  }

  updateRateCardName(rc: RateCard): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/rate-cards/${rc.id}`, { name: rc.name }).pipe(
      tap(() => this.alertify.success('Rate card updated')),
      catchError(err => { this.alertify.error('Failed to update rate card'); return throwError(() => err); })
    );
  }

  deleteRateCard(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/rate-cards/${id}`).pipe(
      tap(() => {
        const i = this.rateCards.findIndex(r => r.id === id);
        if (i !== -1) this.rateCards.splice(i, 1);
        this.alertify.success('Rate card deleted');
      }),
      catchError(err => { this.alertify.error('Failed to delete rate card'); return throwError(() => err); })
    );
  }

  // ── RC Tiers ───────────────────────────────────────────────────────────────

  addRcTier(rateCardId: string): Observable<void> {
    return this.http
      .post<ApiRcTier>(`${this.baseUrl}/rate-cards/${rateCardId}/tiers`, { name: 'New Tier', rate: 0 })
      .pipe(
        tap(t => {
          const rc = this.rateCards.find(r => r.id === rateCardId);
          if (rc) rc.tiers.push({ id: t.id, name: t.name, rate: t.rate });
          this.alertify.success('Tier added');
        }),
        map(() => undefined as void),
        catchError(err => { this.alertify.error('Failed to add tier'); return throwError(() => err); })
      );
  }

  updateRcTier(rateCardId: string, tier: RcTier): Observable<void> {
    return this.http
      .put<void>(`${this.baseUrl}/rate-cards/${rateCardId}/tiers/${tier.id}`, { name: tier.name, rate: tier.rate })
      .pipe(
        tap(() => this.alertify.success('Tier updated')),
        catchError(err => { this.alertify.error('Failed to update tier'); return throwError(() => err); })
      );
  }

  deleteRcTier(rateCardId: string, tierId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/rate-cards/${rateCardId}/tiers/${tierId}`).pipe(
      tap(() => {
        const rc = this.rateCards.find(r => r.id === rateCardId);
        if (rc) {
          const i = rc.tiers.findIndex(t => t.id === tierId);
          if (i !== -1) rc.tiers.splice(i, 1);
        }
        this.alertify.success('Tier deleted');
      }),
      catchError(err => { this.alertify.error('Failed to delete tier'); return throwError(() => err); })
    );
  }

  // ── RC Addons ──────────────────────────────────────────────────────────────

  addRcAddon(rateCardId: string): Observable<void> {
    return this.http
      .post<ApiRcAddon>(`${this.baseUrl}/rate-cards/${rateCardId}/addons`, { name: 'New Add-on', price: 0 })
      .pipe(
        tap(a => {
          const rc = this.rateCards.find(r => r.id === rateCardId);
          if (rc) rc.addons.push({ id: a.id, name: a.name, price: a.price });
          this.alertify.success('Add-on added');
        }),
        map(() => undefined as void),
        catchError(err => { this.alertify.error('Failed to add add-on'); return throwError(() => err); })
      );
  }

  updateRcAddon(rateCardId: string, addon: RcAddon): Observable<void> {
    return this.http
      .put<void>(`${this.baseUrl}/rate-cards/${rateCardId}/addons/${addon.id}`, { name: addon.name, price: addon.price })
      .pipe(
        tap(() => this.alertify.success('Add-on updated')),
        catchError(err => { this.alertify.error('Failed to update add-on'); return throwError(() => err); })
      );
  }

  deleteRcAddon(rateCardId: string, addonId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/rate-cards/${rateCardId}/addons/${addonId}`).pipe(
      tap(() => {
        const rc = this.rateCards.find(r => r.id === rateCardId);
        if (rc) {
          const i = rc.addons.findIndex(a => a.id === addonId);
          if (i !== -1) rc.addons.splice(i, 1);
        }
        this.alertify.success('Add-on deleted');
      }),
      catchError(err => { this.alertify.error('Failed to delete add-on'); return throwError(() => err); })
    );
  }

  // ── Mappers ────────────────────────────────────────────────────────────────

  private mapCatalog(s: ApiCatalogService): CatalogService {
    return {
      id:         s.id,
      name:       s.name,
      type:       s.serviceType as ServiceType,
      fixedRate:  s.fixedRate,
      hourlyRate: s.hourlyRate,
      markup:     s.markup,
      pts:        s.pts ?? [],
      rateCardId: s.rateCardId,
    };
  }

  /**
   * Add templates a bid references that were removed from the project type (each already `deleted: true`),
   * so the bid wizard can resolve their name/type/rates. Idempotent — skips ids already present.
   */
  mergeTemplates(projectTypeId: string, rows: TemplateRow[]): void {
    if (!projectTypeId || !rows?.length) return;
    const existing = (this.ptData[projectTypeId] ??= []);
    for (const row of rows) {
      if (!existing.some(t => t.id === row.id)) existing.push({ ...row, deleted: true });
    }
  }

  private mapTemplate(t: ApiTemplateRow): TemplateRow {
    return {
      id:              t.id,
      projectTypeId:   t.projectTypeId,
      projectTypeName: t.projectTypeName,
      name:            t.name,
      type:            t.serviceType as ServiceType,
      fixedRate:       t.fixedRate,
      hourlyRate:      t.hourlyRate,
      hours:           t.hours,
      markup:          t.markup,
      defaultOn:       t.defaultOn,
      rateCardId:      t.rateCardId,
    };
  }
}
